import { REPORT_RESOLUTIONS, RESOLUTION_NOTE_MAX_LENGTH, type AccountRef } from '@aysar/contracts';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { AccountRefCell } from '@/shared/components/account/AccountRefCell/AccountRefCell';
import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { ErrorState } from '@/shared/components/feedback/ErrorState/ErrorState';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { Badge } from '@/shared/components/ui/Badge/Badge';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { accountPath } from '@/shared/utils/account-path/account-path.util';
import { formatDateTime } from '@/shared/utils/format-datetime/format-datetime.util';

import { useResolveReportModalLogic } from './useResolveReportModalLogic';

/**
 * One report: what was said, and what the reviewer concluded.
 *
 * It **reads** for `moderation.review` and **decides** for `moderation.manage`,
 * in one dialog — see `moderation-modals.config.tsx` for why that is one control
 * and not two. Everything on screen comes from the dialog's own query, so it
 * cannot be showing a report the queue's poll has already moved past.
 *
 * There is deliberately **no ban or suspend button here.** A resolution records
 * a judgement about a *report*; taking an account out of service is a different
 * act with its own containment check (N28), its own audit row and its own
 * appeal, and putting the two side by side would invite a reviewer to reach for
 * the destructive one while thinking they were closing a ticket.
 */
export function ResolveReportModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('moderation');
  const { can } = useHasPermission();
  const { locale } = useDirection();
  const { formats } = useBrand();
  const {
    report,
    isLoading,
    loadError,
    reload,
    isDecided,
    canResolve,
    resolution,
    setResolution,
    note,
    setNote,
    canSubmit,
    isSaving,
    error,
    isConflict,
    isGone,
    submit,
  } = useResolveReportModalLogic(modalData, onCancel);

  const account = (ref: AccountRef | null, label: string) => (
    <div className="flex flex-col gap-2xs">
      <span className="text-caption text-muted-foreground">{label}</span>
      <AccountRefCell
        account={ref}
        href={accountPath(ref, can)}
        caption={
          ref === null
            ? undefined
            : ref.statusReason === null
              ? t('resolve.noStatusReason')
              : t('resolve.statusReason', { reason: ref.statusReason })
        }
      />
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('resolve.title')}</DialogTitle>
          <DialogDescription>{t('resolve.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <div className="flex flex-col gap-md" aria-label={t('resolve.loading')}>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : loadError !== null ? (
            <ErrorState error={loadError} title={t('resolve.loadFailed')} onRetry={reload} />
          ) : report === null ? null : (
            <div className="flex flex-col gap-lg">
              <div className="grid gap-md sm:grid-cols-2">
                {account(report.reported, t('resolve.reported'))}
                {account(report.reporter, t('resolve.reporter'))}
              </div>

              {report.reported?.status === 'banned' || report.reported?.status === 'suspended' ? (
                <p className="flex items-start gap-sm text-body text-muted-foreground">
                  <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t('resolve.alreadyOutOfService')}</span>
                </p>
              ) : null}

              <div className="flex flex-wrap items-center gap-sm">
                <Badge tone="neutral">{t(`reason.${report.reason}`)}</Badge>
                <span className="text-caption text-muted-foreground">
                  {t('resolve.context')}: {t(`context.${report.context}`)}
                </span>
                <span className="text-caption text-muted-foreground">
                  {formatDateTime(report.reportedAt, locale, formats)}
                </span>
                {report.openReportsAgainstMember > 1 ? (
                  <Badge tone="warning">
                    <span className="tabular-nums">
                      {t('queue.openReports', { count: report.openReportsAgainstMember })}
                    </span>
                  </Badge>
                ) : null}
              </div>

              {report.contextRef === null ? null : (
                <div className="flex flex-col gap-2xs rounded-md bg-muted p-md">
                  <span dir="ltr" className="text-caption">
                    {t('resolve.contextRef', { id: report.contextRef })}
                  </span>
                  {/* Named, never linked: a message is member content, and it is
                      read through the surface that composes it under its own
                      permissions and audit (N12). */}
                  <span className="text-caption text-muted-foreground">
                    {t('resolve.contextRefHint')}
                  </span>
                </div>
              )}

              <section className="flex flex-col gap-xs">
                <h3 className="text-caption font-medium text-foreground">{t('resolve.note')}</h3>
                {report.note === null ? (
                  <p className="text-body text-muted-foreground">{t('resolve.noNote')}</p>
                ) : (
                  // The reporter's own words, in full and unreflowed — the
                  // dialog body is the one scrolling region, so 2000 characters
                  // are readable rather than truncated.
                  <p className="whitespace-pre-wrap break-words text-body">{report.note}</p>
                )}
              </section>

              {isDecided ? (
                <section className="flex flex-col gap-xs rounded-md bg-muted p-md">
                  <h3 className="text-caption font-medium text-foreground">
                    {t('resolve.decided.title')}
                  </h3>
                  <span className="text-body">
                    {report.resolution === null ? null : t(`resolution.${report.resolution}`)}
                  </span>
                  <span className="text-caption text-muted-foreground">
                    {t(
                      report.resolvedByUserId === null
                        ? 'resolve.decided.bySystem'
                        : 'resolve.decided.by',
                      {
                        when:
                          report.resolvedAt === null
                            ? ''
                            : formatDateTime(report.resolvedAt, locale, formats),
                      },
                    )}
                  </span>
                  <span className="text-caption text-muted-foreground">
                    {report.resolutionNote ?? t('resolve.decided.noNote')}
                  </span>
                </section>
              ) : canResolve ? (
                <>
                  <ChoiceCards
                    label={t('resolve.verdict')}
                    items={REPORT_RESOLUTIONS.map((value) => ({
                      value,
                      label: t(`resolution.${value}`),
                      description: t(`resolutionHint.${value}`),
                    }))}
                    value={resolution}
                    onValueChange={(value) => {
                      // Narrowed by construction: the cards are built from
                      // `REPORT_RESOLUTIONS`, so nothing else can come back.
                      setResolution(value as (typeof REPORT_RESOLUTIONS)[number]);
                    }}
                  />

                  <div className="flex flex-col gap-xs">
                    <Label htmlFor="moderation-resolution-note">{t('resolve.reviewerNote')}</Label>
                    <Textarea
                      id="moderation-resolution-note"
                      aria-describedby="moderation-resolution-note-hint"
                      value={note}
                      maxLength={RESOLUTION_NOTE_MAX_LENGTH}
                      onChange={(event) => {
                        setNote(event.target.value);
                      }}
                    />
                    <p
                      id="moderation-resolution-note-hint"
                      className="text-caption text-muted-foreground"
                    >
                      {t('resolve.reviewerNoteHint')}
                    </p>
                  </div>
                </>
              ) : (
                <p className="flex items-start gap-sm text-body text-muted-foreground">
                  <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t('resolve.readOnly')}</span>
                </p>
              )}

              {error === null ? null : (
                // Drawn HERE and not as a toast: a colleague's verdict is news
                // the reviewer has to act on, and a toast takes it away before
                // it has been read.
                <div
                  role="alert"
                  className="flex flex-col gap-sm rounded-md border border-destructive p-md"
                >
                  <p className="flex items-start gap-sm text-body text-destructive">
                    <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </p>
                  {isConflict || isGone ? (
                    <div>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          // A re-read, never a retry: the api would refuse the
                          // same verdict again, and what the reviewer needs is
                          // the decision somebody else already made.
                          if (isGone) {
                            onCancel();
                          } else {
                            reload();
                          }
                        }}
                      >
                        {t(isGone ? 'resolve.close' : 'resolve.refresh')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('resolve.close')}
          </Button>

          {canResolve ? (
            <Button
              isLoading={isSaving}
              disabled={!canSubmit}
              onClick={() => {
                void submit();
              }}
            >
              {t('resolve.submit')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
